import { useState } from 'react';

type LoginPageProps = { onLoginSuccess: (token: string) => void };

export function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState<string | null>(null);
  const relayUrl = process.env.REACT_APP_RELAY_SERVER_URL || 'ws://localhost:8081';
  const authUrl = relayUrl.replace(/^ws(s?):\/\//, 'http$1://') + '/auth';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await fetch(authUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user, pass }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem('authToken', data.token);
        onLoginSuccess(data.token);
      } else {
        setError(data.error || 'Usuário ou senha inválidos.');
      }
    } catch {
      setError('Erro ao conectar ao servidor.');
    }
  };

  const C = {
    green:      '#006837',
    greenMid:   '#2E8B57',
    greenLight: '#52B788',
    bgPage:     '#EEF7F2',
    bgInput:    '#FFFFFF',
    card:       '#FFFFFF',
    text:       '#1C2B22',
    textMuted:  '#5A7465',
    border:     '#C8E6D5',
    focusRing:  'rgba(0,104,55,0.18)',
    errorText:  '#C0392B',
    errorBg:    '#FFF5F5',
    errorBorder:'#F5C6CB',
  };

  const inputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    padding: '12px 16px',
    borderRadius: 12,
    border: `1.5px solid ${C.border}`,
    marginTop: 8,
    outline: 'none',
    background: C.bgInput,
    color: C.text,
    fontSize: 15,
    boxSizing: 'border-box',
    transition: 'border-color .18s, box-shadow .18s',
    fontFamily: 'inherit',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: C.bgPage,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Círculos decorativos de fundo */}
      <div aria-hidden style={{
        position: 'absolute', top: -120, left: -120,
        width: 420, height: 420, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(0,104,55,0.12) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div aria-hidden style={{
        position: 'absolute', bottom: -80, right: -80,
        width: 340, height: 340, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(46,139,87,0.10) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />
      <div aria-hidden style={{
        position: 'absolute', top: '40%', right: '8%',
        width: 180, height: 180, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(82,183,136,0.08) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 420,
          padding: '44px 36px 32px',
          borderRadius: 20,
          background: C.card,
          boxShadow: '0 8px 40px rgba(0,80,40,0.10), 0 2px 8px rgba(0,0,0,0.04)',
          border: `1px solid rgba(200,230,213,0.7)`,
          color: C.text,
          position: 'relative',
        }}
      >
        {/* Faixa superior */}
        <div aria-hidden style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 5,
          borderRadius: '20px 20px 0 0',
          background: `linear-gradient(90deg, ${C.green} 0%, ${C.greenLight} 100%)`,
        }} />

        {/* Cabeçalho */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 64, height: 64, borderRadius: '50%',
            background: `linear-gradient(145deg, ${C.green} 0%, ${C.greenMid} 100%)`,
            marginBottom: 16,
            boxShadow: `0 6px 20px rgba(0,104,55,0.28)`,
          }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" fill="white"/>
            </svg>
          </div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.green, letterSpacing: '-0.3px' }}>
            Acesso ao Assistente
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 13.5, color: C.textMuted }}>
            Prefeitura Municipal de Cariacica
          </p>
        </div>

        {/* Campo Usuário */}
        <label style={{ display: 'block', marginBottom: 18, fontSize: 14, fontWeight: 600, color: C.text }}>
          Usuário
          <input
            type="text"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            autoComplete="username"
            placeholder="Digite seu usuário"
            style={inputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = C.greenMid;
              e.currentTarget.style.boxShadow = `0 0 0 3px ${C.focusRing}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </label>

        {/* Campo Senha */}
        <label style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 600, color: C.text }}>
          Senha
          <input
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            autoComplete="current-password"
            placeholder="Digite sua senha"
            style={inputStyle}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = C.greenMid;
              e.currentTarget.style.boxShadow = `0 0 0 3px ${C.focusRing}`;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = C.border;
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        </label>

        {error && (
          <div role="alert" style={{
            color: C.errorText, background: C.errorBg,
            border: `1px solid ${C.errorBorder}`,
            borderRadius: 10, padding: '10px 14px',
            marginTop: 14, fontSize: 13.5,
          }}>
            {error}
          </div>
        )}

        {/* Botão */}
        <button
          type="submit"
          style={{
            width: '100%',
            padding: '14px 16px',
            borderRadius: 12,
            border: 'none',
            cursor: 'pointer',
            background: `linear-gradient(135deg, ${C.green} 0%, ${C.greenMid} 100%)`,
            color: '#FFFFFF',
            fontWeight: 700,
            fontSize: 15.5,
            letterSpacing: '0.2px',
            transition: 'transform .08s ease, box-shadow .2s ease, filter .15s',
            boxShadow: `0 4px 16px rgba(0,104,55,0.30)`,
            marginTop: 22,
            fontFamily: 'inherit',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.filter = 'brightness(1.08)';
            e.currentTarget.style.boxShadow = `0 6px 22px rgba(0,104,55,0.40)`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.filter = 'brightness(1)';
            e.currentTarget.style.boxShadow = `0 4px 16px rgba(0,104,55,0.30)`;
          }}
          onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
          onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
        >
          Entrar
        </button>

        <p style={{ textAlign: 'center', marginTop: 24, marginBottom: 0, fontSize: 12, color: C.textMuted }}>
          © {new Date().getFullYear()} Prefeitura Municipal de Cariacica
        </p>
      </form>
    </div>
  );
}